import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2, Eraser, Code2, Palette } from 'lucide-react';

/**
 * Lightweight WYSIWYG editor used for mailing bodies.
 *
 * Design goals (dall'utente):
 * - "come viene scritto cosi viene inviato": preserva spazi multipli, tab e
 *   interruzioni di riga tramite `white-space: pre-wrap` sia nell'editor che
 *   nel preview finale.
 * - Grassetti/corsivi/liste/link gestiti via toolbar + document.execCommand,
 *   nessuna dipendenza esterna.
 * - Output HTML pulito compatibile con Brevo. Il placeholder `{{azienda}}`
 *   continua a funzionare perché viene preservato come text node.
 */
const btn = 'inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-slate-200 text-slate-700 active:bg-slate-300';

const COLORS = [
  ['Nero', '#000000'], ['Grigio', '#64748b'], ['Rosso', '#dc2626'], ['Arancione', '#ea580c'],
  ['Giallo', '#ca8a04'], ['Verde', '#16a34a'], ['Blu', '#023f80'], ['Azzurro', '#0284c7'], ['Viola', '#7c3aed'],
];

const ColorPicker = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const pick = (c) => { onPick(c); setOpen(false); };
  return (
    <div className="relative">
      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((o) => !o)} className={btn} title="Colore testo" data-testid="rte-color">
        <Palette size={14} />
      </button>
      {open && (
        <div className="absolute z-20 top-8 left-0 p-2 bg-white border border-slate-200 rounded-lg shadow-lg flex flex-wrap gap-1 w-[150px]" data-testid="rte-color-menu">
          {COLORS.map(([label, c]) => (
            <button key={c} type="button" title={label} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(c)}
              className="w-6 h-6 rounded-full border border-slate-300 hover:scale-110 transition-transform" style={{ background: c }}
              data-testid={`rte-color-${label.toLowerCase()}`} />
          ))}
          <label className="w-full mt-1 flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer" onMouseDown={(e) => e.preventDefault()}>
            Altro: <input type="color" className="w-8 h-6 p-0 border-0 cursor-pointer" onChange={(e) => pick(e.target.value)} data-testid="rte-color-custom" />
          </label>
        </div>
      )}
    </div>
  );
};

const RichTextEditor = ({ value, onChange, placeholder, testId }) => {
  const ref = useRef(null);
  const lastExternalRef = useRef(value || '');

  // Allinea il contenuto quando `value` cambia DALL'ESTERNO (es: click su template).
  // Non toccare il DOM a ogni onInput: romperebbe la posizione del cursore.
  useEffect(() => {
    if (!ref.current) return;
    if (value === lastExternalRef.current) return;
    lastExternalRef.current = value || '';
    if (ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const emit = useCallback(() => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastExternalRef.current = html;
    onChange?.(html);
  }, [onChange]);

  const exec = (command, arg) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const savedRange = useRef(null);
  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current?.contains(sel.anchorNode)) savedRange.current = sel.getRangeAt(0).cloneRange();
  };

  const setColor = (c) => {
    ref.current?.focus();
    if (savedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    document.execCommand('styleWithCSS', false, true);
    document.execCommand('foreColor', false, c);
    document.execCommand('styleWithCSS', false, false);
    emit();
  };

  const insertLink = () => {
    const sel = window.getSelection();
    const current = sel && !sel.isCollapsed ? sel.toString() : '';
    const url = window.prompt('URL (es. https://…):', 'https://');
    if (!url) return;
    if (current) {
      exec('createLink', url);
    } else {
      const label = window.prompt('Testo del link:', url) || url;
      const html = `<a href="${url}" target="_blank" rel="noreferrer">${label}</a>`;
      exec('insertHTML', html);
    }
  };

  const insertPlaceholder = (tag) => {
    exec('insertText', `{{${tag}}}`);
  };

  // Converte paste di testo ricco in testo semplice preservando newline
  // (evita l'importazione di stili inline da Word/Mail).
  const onPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <div className="flex items-center gap-0.5 px-2 py-1 bg-slate-50 rounded-t-lg border-b border-slate-200 flex-wrap">
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} className={btn} title="Grassetto (Ctrl+B)" data-testid="rte-bold"><Bold size={14} /></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')} className={btn} title="Corsivo (Ctrl+I)" data-testid="rte-italic"><Italic size={14} /></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')} className={btn} title="Sottolineato (Ctrl+U)" data-testid="rte-underline"><UnderlineIcon size={14} /></button>
        <ColorPicker onPick={setColor} />
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')} className={btn} title="Elenco puntato" data-testid="rte-ul"><List size={14} /></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertOrderedList')} className={btn} title="Elenco numerato" data-testid="rte-ol"><ListOrdered size={14} /></button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={insertLink} className={btn} title="Inserisci link" data-testid="rte-link"><Link2 size={14} /></button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('removeFormat')} className={btn} title="Rimuovi formattazione" data-testid="rte-clear"><Eraser size={14} /></button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertPlaceholder('azienda')} className="inline-flex items-center gap-1 px-2 h-7 rounded-md text-[11px] font-bold bg-amber-100 text-amber-800 hover:bg-amber-200" title="Inserisci il nome dell'azienda nel punto del cursore" data-testid="rte-placeholder-azienda">
          <Code2 size={11} /> {'{{azienda}}'}
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={() => { saveRange(); emit(); }}
        onKeyUp={saveRange}
        onMouseUp={saveRange}
        onPaste={onPaste}
        data-placeholder={placeholder}
        data-testid={testId ? `${testId}-area` : 'rte-area'}
        className="rte-editor min-h-[220px] max-h-[440px] overflow-y-auto p-3 text-sm outline-none focus:ring-2 focus:ring-[#023F80]/20"
        style={{ whiteSpace: 'pre-wrap' }}
      />
    </div>
  );
};

export default RichTextEditor;
